import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAsyncError } from '@/modules/errors'
import { type Folder, useFolders, useLoadingFolders } from '@/modules/folders'
import type { DocumentsSearchBlock } from '../stores/types'
import { useDocumentsLoaders } from './documents'

export const useSearchDocuments = () => {
  const searchParams = useSearch({ strict: false })
  const navigate = useNavigate()

  const [results, setResults] = useState<DocumentsSearchBlock[]>([])
  const [query, setQuery] = useState(searchParams.q?.toString() || '')
  const [folder, setFolder] = useState<Folder | null>(null)
  const loaders = useDocumentsLoaders()
  const throwError = useAsyncError()
  const folders = useFolders()
  const isLoadingFolders = useLoadingFolders()
  const isMounted = useRef(false)

  const setFolderSlug = useCallback(
    (slug: string) => {
      if (slug === 'all') {
        return setFolder(null)
      }
      const folder = folders.find((folder) => folder?.slug === slug)
      setFolder(folder || null)
    },
    [folders],
  )

  useEffect(() => {
    setFolderSlug(searchParams.folder)
    if (!isLoadingFolders) {
      isMounted.current = true
    }
  }, [setFolderSlug, searchParams.folder])

  useEffect(() => {
    if (!isMounted.current) return

    navigate({
      to: '/documents/search',
      search: {
        q: query || undefined,
        folder: folder?.slug || undefined,
      },
      replace: true,
    })
  }, [query, folder])

  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      return
    }

    const timer = setTimeout(() => {
      loaders
        .searchDocuments(query, folder?.id ?? null)
        .then((results) => setResults(results))
        .catch(throwError)
    }, 300)

    return () => clearTimeout(timer)
  }, [query, folder])

  return {
    results,
    query,
    setQuery,
    folderSlug: folder?.slug || null,
    setFolderSlug,
  }
}
